import { useCallback, useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { Icon } from '../lib/icons';
import { Logo } from './ui';
import { GoogleButton } from './GoogleButton';
import { AppleButton } from './AppleButton';

export function AuthScreen({ onToken, initialMode = 'login', onBack }: { onToken: () => void; initialMode?: 'login' | 'register'; onBack?: () => void }) {
  const [mode, setMode] = useState<'login' | 'register'>(initialMode);
  const [form, setForm] = useState({ name: '', teamName: '', email: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const googleError = useCallback((msg: string) => setError(msg), []);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = mode === 'login' ? { email: form.email, password: form.password } : form;
      const res = await api<{ token: string }>(`/auth/${mode}`, { method: 'POST', json: body });
      onToken();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Das hat nicht geklappt');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-full place-items-center p-4">
      <div className="w-full max-w-[420px]">
        <div className="mb-8 flex justify-center">
          <button onClick={onBack} aria-label="Zur Startseite" disabled={!onBack} className="disabled:opacity-100"><Logo size={44} claim /></button>
        </div>
        <form className="card flex flex-col gap-4 p-6" onSubmit={submit}>
          <div>
            <h1 className="display m-0 text-[26px]">{mode === 'login' ? 'Anmelden' : 'Team anlegen'}</h1>
            <p className="m-0 mt-1 text-[var(--muted)]">
              {mode === 'login' ? 'Deine Aufgaben warten schon.' : 'Aufgaben besprechen wie im Chat.'}
            </p>
          </div>

          {mode === 'register' && (
            <>
              <label className="field">Dein Name<input className="input" value={form.name} onChange={set('name')} autoComplete="name" required /></label>
              <label className="field">Name deines Teams oder deiner Firma<input className="input" value={form.teamName} onChange={set('teamName')} autoComplete="organization" required /></label>
            </>
          )}
          <label className="field">E-Mail<input className="input" type="email" value={form.email} onChange={set('email')} autoComplete="email" required /></label>
          <label className="field">
            Passwort
            <input className="input" type="password" value={form.password} onChange={set('password')} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} minLength={mode === 'register' ? 8 : 1} required />
          </label>

          {error && (
            <div className="check" role="alert"><Icon name="alert" size={18} />{error}</div>
          )}

          <button className="btn btn-primary mt-1" disabled={busy}>
            {busy ? 'Einen Moment …' : mode === 'login' ? 'Anmelden' : 'Team anlegen'}
          </button>
          <GoogleButton key={mode} mode={mode} teamName={form.teamName} onToken={onToken} onError={googleError} />
          <AppleButton mode={mode} teamName={form.teamName} onToken={onToken} onError={googleError} />
        </form>
        <p className="mt-5 text-center text-[var(--muted)]">
          {mode === 'login' ? 'Noch kein Konto?' : 'Schon registriert?'}{' '}
          <button className="font-semibold text-[var(--brand-a)]" onClick={() => (setMode(mode === 'login' ? 'register' : 'login'), setError(null))}>
            {mode === 'login' ? 'Team anlegen' : 'Anmelden'}
          </button>
        </p>
      </div>
    </div>
  );
}
